import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import {
  UserCog,
  CalendarPlus,
  KeyRound,
  UserX,
  Download,
  ChevronRight,
  ArrowRight,
  TrendingUp,
  Infinity as InfinityIcon,
  CircleCheckBig,
  BookOpen,
  LogIn,
  ExternalLink,
  RefreshCw,
  CircleArrowUp,
  CirclePause,
  CircleX,
  Plus,
  MoreVertical,
  Info,
  CreditCard,
  Ban,
  Gift,
  Check,
  Calendar,
  ChevronDown,
  Car,
  Truck,
  Plane,
} from "lucide-react";
import AdminPortalShell from "@/components/admin/AdminPortalShell";
import AdminTable from "@/components/admin/AdminTable";
import {
  getUserById,
  ENTITLEMENTS_BY_USER,
  CHARGES_BY_USER,
  type AdminUser,
  type Entitlement,
  type Charge,
} from "@/components/admin/_mockData";

type TabKey =
  | "overview"
  | "billing"
  | "entitlements"
  | "sessions"
  | "audit";

const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "billing", label: "Subscription & Billing" },
  { key: "entitlements", label: "Entitlements & Access" },
  { key: "sessions", label: "Sessions & Activity" },
  { key: "audit", label: "Audit Log" },
];

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = id ? getUserById(id) : undefined;
  const [tab, setTab] = useState<TabKey>("overview");

  if (!user) {
    return (
      <AdminPortalShell title="User not found">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-600">
          No user with id <code className="font-mono">{id}</code>.{" "}
          <Link to="/admin/users" className="text-indigo-600">
            Back to users
          </Link>
          .
        </div>
      </AdminPortalShell>
    );
  }

  const ents = ENTITLEMENTS_BY_USER[user.id] ?? [];

  return (
    <AdminPortalShell title={user.name} testId="admin-user-detail-page">
      <Breadcrumbs user={user} />
      <ProfileHeader user={user} variant={tab === "overview" ? "full" : "slim"} />
      <TabStrip current={tab} onChange={setTab} />
      <div className="mt-5">
        {tab === "overview" && <OverviewTab user={user} ents={ents} />}
        {tab === "billing" && <BillingTab user={user} />}
        {tab === "entitlements" && <EntitlementsTab ents={ents} />}
        {tab === "sessions" && <StubTab label="Sessions & Activity" />}
        {tab === "audit" && (
          <StubTab
            label="Audit Log (per-user)"
            note="See the global Audit Log page; filter will land in ROADS-476 deliverable D1."
          />
        )}
      </div>
    </AdminPortalShell>
  );
}

function Breadcrumbs({ user }: { user: AdminUser }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-4 flex items-center gap-2 text-xs font-medium text-slate-500"
    >
      <span>Admin</span>
      <ChevronRight aria-hidden="true" className="w-3 h-3 text-slate-300" />
      <Link to="/admin/users" className="text-slate-500 no-underline hover:text-slate-700">
        Users
      </Link>
      <ChevronRight aria-hidden="true" className="w-3 h-3 text-slate-300" />
      <span className="font-semibold text-slate-700">{user.name}</span>
    </nav>
  );
}

function ProfileHeader({
  user,
  variant,
}: {
  user: AdminUser;
  variant: "full" | "slim";
}) {
  if (variant === "slim") {
    return (
      <div className="mb-5 flex items-center gap-5 rounded-2xl border border-slate-200 bg-white p-5">
        <Avatar user={user} size={56} fontSize="lg" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-semibold text-slate-900">
            {user.name}
          </div>
          <div className="truncate text-xs text-slate-500">
            {user.email} · {user.id.slice(0, 12)}…{user.id.slice(-5)} · focus{" "}
            {user.focusState ?? "—"}
            {user.cdlInterest ? " · CDL interest" : ""}
          </div>
        </div>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          <ExternalLink aria-hidden="true" className="w-3.5 h-3.5" />
          Open in Stripe Dashboard
        </button>
      </div>
    );
  }
  return (
    <div className="mb-5 flex items-start gap-6 rounded-2xl border border-slate-200 bg-white p-6">
      <Avatar user={user} size={80} fontSize="xl" />
      <div className="min-w-0 flex-1 space-y-3.5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-2xl font-semibold text-slate-900">
            {user.name}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 rounded-full bg-emerald-500"
            />
            Active
          </span>
          <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">
            Trial · {user.trialDaysLeft}d left
          </span>
        </div>
        <div className="flex flex-wrap gap-7">
          <MetaItem label="EMAIL" value={user.email} />
          <MetaItem
            label="WORKOS ID"
            value={`${user.id.slice(0, 12)}…${user.id.slice(-5)}`}
          />
          <MetaItem
            label="HOME"
            value={`${user.homeCountryEmoji} ${user.homeCountry} · ${user.language}`}
          />
          <MetaItem label="FOCUS STATE" value={user.focusState ?? "—"} />
          <MetaItem
            label="SIGNED UP"
            value={user.signupAt.replace("T", " · ").replace("Z", " UTC")}
          />
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="flex gap-2">
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <UserCog aria-hidden="true" className="w-3.5 h-3.5" />
            Impersonate
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <CalendarPlus aria-hidden="true" className="w-3.5 h-3.5" />
            Extend trial
          </button>
        </div>
        <div className="flex gap-2">
          <GhostButton icon={KeyRound} label="Reset MFA" />
          <GhostButton icon={UserX} label="Disable" />
          <GhostButton icon={Download} label="Export GDPR" />
        </div>
      </div>
    </div>
  );
}

function Avatar({
  user,
  size,
  fontSize,
}: {
  user: AdminUser;
  size: number;
  fontSize: "lg" | "xl";
}) {
  return (
    <div
      aria-hidden="true"
      className={`flex items-center justify-center rounded-full font-bold text-white ${
        fontSize === "xl" ? "text-[26px]" : "text-base"
      }`}
      style={{ backgroundColor: user.avatarHex, width: size, height: size }}
    >
      {user.initials}
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}

function GhostButton({
  icon: Icon,
  label,
}: {
  icon: typeof KeyRound;
  label: string;
}) {
  return (
    <button
      type="button"
      className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
    >
      <Icon aria-hidden="true" className="w-3 h-3" />
      {label}
    </button>
  );
}

function TabStrip({
  current,
  onChange,
}: {
  current: TabKey;
  onChange: (k: TabKey) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="User detail sections"
      className="flex items-end gap-0 border-b border-slate-200"
    >
      {TABS.map((t) => {
        const active = t.key === current;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            type="button"
            className={`px-4 pb-3.5 pt-3 text-sm transition-colors ${
              active
                ? "border-b-2 border-indigo-600 font-semibold text-slate-900"
                : "font-medium text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`admin-user-detail-tab-${t.key}`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function StubTab({ label, note }: { label: string; note?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
      <div className="font-semibold text-slate-700">{label}</div>
      <div className="mt-1.5 text-xs">{note ?? "Coming in a follow-up PR."}</div>
    </div>
  );
}

function OverviewTab({
  user,
  ents,
}: {
  user: AdminUser;
  ents: Entitlement[];
}) {
  const cdlCount = ents.filter((e) => e.tier === "cdl").length;
  const classCStates = ents.filter(
    (e) => e.tier === "class_c" && e.stateCode !== null,
  ).length;
  const touristIn = ents.filter((e) => e.tier === "tourist_in").length;

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="flex-1 space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard
            label="ENTITLEMENTS"
            value={String(ents.length)}
            sub={`${classCStates} Class C · ${cdlCount} CDL · ${touristIn} Tourist-In`}
            tone="good"
          />
          <StatCard
            label="SUBSCRIPTION"
            value="None yet"
            sub="Trial only — converts in 30d"
          />
          <StatCard
            label="PRACTICE SESSIONS"
            value="12"
            sub="Last 7 days · 84% pass rate"
            tone="good"
          />
          <StatCard
            label="FLAGS"
            value="0"
            sub="No abuse / risk signals"
            tone="good"
          />
        </div>
        <Card>
          <CardHeader title="Recent activity">
            <Link
              to="#"
              className="flex items-center gap-1 text-[11px] font-semibold text-indigo-700 no-underline"
            >
              View Sessions tab
              <ArrowRight aria-hidden="true" className="w-2.5 h-2.5" />
            </Link>
          </CardHeader>
          <Activity
            icon={CircleCheckBig}
            iconColor="text-emerald-600"
            title="Completed practice test"
            sub="Class C · TX · 84% (42/50) · 12 min ago"
          />
          <Activity
            icon={BookOpen}
            iconColor="text-indigo-600"
            title="Read rule diff: Right turn on red — IN → TX"
            sub="Rule-diff session · 38 min ago"
          />
          <Activity
            icon={LogIn}
            iconColor="text-slate-500"
            title="Signed in"
            sub="Pixel 8 · Chrome 122 · Houston, TX · 1h ago"
          />
          <Activity
            icon={UserCog}
            iconColor="text-indigo-600"
            title="Account created via WorkOS"
            sub={`${user.signupAt.replace("T", " ").replace("Z", " UTC")} · trial auto-granted (30d)`}
            last
          />
        </Card>
      </div>
      <aside className="w-full space-y-4 lg:w-[380px]">
        <Card padded>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              Trial clock
            </h3>
            <div className="flex-1" />
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
              TEMP 30d
            </span>
          </div>
          <div className="mt-3">
            <div className="text-4xl font-bold text-slate-900">29d 11h</div>
            <div className="mt-1 text-xs font-medium text-slate-500">
              Until {user.trialExpiresAt.replace("T", " ").replace("Z", " UTC")}
            </div>
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full w-[14px] rounded-full bg-indigo-600" />
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
            Code constant is temporarily 30 days while content + functionality
            are under construction — will re-tune post-content.
          </p>
        </Card>
        <Card padded>
          <h3 className="text-sm font-semibold text-slate-900">
            Comp entitlements
          </h3>
          <div className="mt-3 space-y-2">
            {ents
              .filter((e) => e.source === "comp")
              .slice(0, 4)
              .map((e) => (
                <div
                  key={e.id}
                  className="flex items-center gap-2.5 rounded-lg bg-slate-50 px-3 py-2.5"
                >
                  <InfinityIcon
                    aria-hidden="true"
                    className="w-3.5 h-3.5 text-indigo-600"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold text-slate-900">
                      {tierLabel(e.tier)}{e.stateCode ? ` · ${e.stateCode}` : ""}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      expires never
                    </div>
                  </div>
                </div>
              ))}
          </div>
          {ents.filter((e) => e.source === "comp").length > 4 && (
            <Link
              to="#"
              className="mt-2 inline-block text-[11px] font-semibold text-indigo-700 no-underline"
            >
              +{ents.filter((e) => e.source === "comp").length - 4} more · view
              Entitlements tab
            </Link>
          )}
        </Card>
      </aside>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: "good" | "warn";
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-[22px] font-semibold text-slate-900">
        {value}
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        {tone === "good" && (
          <TrendingUp
            aria-hidden="true"
            className="w-3 h-3 text-emerald-600"
          />
        )}
        <div
          className={`text-[11px] font-medium ${
            tone === "warn" ? "text-amber-700" : "text-slate-500"
          }`}
        >
          {sub}
        </div>
      </div>
    </div>
  );
}

function Card({
  children,
  padded,
}: {
  children: React.ReactNode;
  padded?: boolean;
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-slate-200 bg-white ${
        padded ? "p-5" : ""
      }`}
    >
      {children}
    </div>
  );
}

function CardHeader({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <div className="flex-1" />
      {children}
    </div>
  );
}

function Activity({
  icon: Icon,
  iconColor,
  title,
  sub,
  last,
}: {
  icon: typeof CircleCheckBig;
  iconColor: string;
  title: string;
  sub: string;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-3.5 px-5 py-3.5 ${
        last ? "" : "border-b border-slate-200"
      }`}
    >
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">
        <Icon aria-hidden="true" className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">
          {title}
        </div>
        <div className="truncate text-[11px] text-slate-500">{sub}</div>
      </div>
    </div>
  );
}

function tierLabel(t: Entitlement["tier"]): string {
  if (t === "class_c") return "Class C";
  if (t === "cdl") return "CDL";
  if (t === "tourist_in") return "Tourist-In";
  if (t === "tourist_out") return "Tourist-Out";
  return "Motorcycle";
}

function tierIcon(t: Entitlement["tier"]) {
  if (t === "cdl") return Truck;
  if (t === "tourist_in" || t === "tourist_out") return Plane;
  return Car;
}

export function BillingTab({ user }: { user: AdminUser }) {
  const charges = CHARGES_BY_USER[user.id] ?? [];
  const chargeColumns = useMemo<ColumnDef<Charge, unknown>[]>(
    () => [
      {
        id: "date",
        // Sort chronologically via parsed timestamp, not alphabetically
        // by the display string ("Apr 17" < "Feb 17" alphabetically is
        // wrong for a date column).
        accessorFn: (c) => new Date(c.date).getTime(),
        header: "Date",
        // TanStack defaults `sortDescFirst` to true for numeric columns;
        // that flips an operator's "click Date once" expectation from
        // "oldest first" to "newest first". Override so first-click is
        // ascending — the visually intuitive default for a date column.
        sortDescFirst: false,
        cell: ({ row }) => (
          <div>
            <div className="text-xs font-semibold text-slate-900">
              {row.original.date}
            </div>
            <div className="font-mono text-[10px] text-slate-500">
              {row.original.id}
            </div>
          </div>
        ),
      },
      {
        id: "description",
        accessorKey: "description",
        header: "Description",
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-slate-900">
              {row.original.description}
            </div>
            <div className="truncate text-[11px] text-slate-500">
              {row.original.method}
            </div>
          </div>
        ),
      },
      {
        id: "amount",
        accessorKey: "amountUsd",
        header: "Amount",
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div
              className={`text-sm font-semibold ${c.amountUsd < 0 ? "text-emerald-700" : "text-slate-900"}`}
            >
              {c.amountUsd < 0 ? "-" : ""}${Math.abs(c.amountUsd).toFixed(2)}
            </div>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const c = row.original;
          const tones: Record<
            Charge["status"],
            { text: string; bg: string; label: string }
          > = {
            paid: { text: "text-emerald-700", bg: "bg-emerald-50", label: "paid" },
            partial_refund: {
              text: "text-amber-700",
              bg: "bg-amber-50",
              label: "partial refund",
            },
            failed: { text: "text-red-700", bg: "bg-red-50", label: "failed" },
            credited: {
              text: "text-indigo-700",
              bg: "bg-indigo-50",
              label: "credited",
            },
          };
          const t = tones[c.status];
          return (
            <span
              className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${t.bg} ${t.text}`}
            >
              {t.label}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const c = row.original;
          const refundable = c.refundable && c.status !== "failed";
          return (
            <div className="flex items-center justify-end gap-1.5">
              {refundable && (
                <button
                  type="button"
                  aria-label={`Refund $${c.amountUsd.toFixed(2)} charge ${c.id}`}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Refund
                </button>
              )}
              <button
                type="button"
                aria-label={`More actions for charge ${c.id}`}
                className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-slate-100"
              >
                <MoreVertical
                  aria-hidden="true"
                  className="h-3 w-3 text-slate-500"
                />
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  if (charges.length === 0) {
    return (
      <StubTab
        label="No subscription yet"
        note={`${user.name} is on trial. Charge history will appear here once a subscription starts.`}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="flex-1 space-y-4">
        <Card>
          <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Current subscription
              </h3>
              <div className="text-[11px] text-slate-500">
                Synced from Stripe · 2 min ago
              </div>
            </div>
            <div className="flex-1" />
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw aria-hidden="true" className="w-3 h-3" />
              Sync now
            </button>
          </div>
          <div className="flex flex-wrap items-start gap-6 p-6">
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-xl font-semibold text-slate-900">
                  CDL prep · Texas
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-emerald-500"
                  />
                  active
                </span>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-3xl font-bold text-slate-900">
                  $14.99
                </span>
                <span className="text-sm font-medium text-slate-500">
                  / month
                </span>
                <span className="text-sm text-slate-300">·</span>
                <span className="font-mono text-[11px] font-medium text-slate-500">
                  price_1QcdlTxStgM2
                </span>
              </div>
              <div className="flex flex-wrap gap-8">
                <MetaItem label="NEXT INVOICE" value="Jun 17, 2026" />
                <MetaItem label="PAYMENT METHOD" value="•••• 4242" />
                <MetaItem label="STARTED" value="May 17, 2026" />
                <MetaItem label="STRIPE SUBSCRIPTION" value="sub_1QcdlT…" />
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <PlanActionButton
                icon={CircleArrowUp}
                label="Change plan"
                kind="primary"
              />
              <PlanActionButton
                icon={CirclePause}
                label="Cancel at period end"
              />
              <PlanActionButton
                icon={CircleX}
                label="Cancel immediately"
                kind="danger"
              />
            </div>
          </div>
        </Card>
        <div className="space-y-3">
          <div className="flex items-center gap-3 px-1">
            <h3 className="text-sm font-semibold text-slate-900">
              Payment history
            </h3>
            <div className="flex-1" />
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-1.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100"
            >
              <Plus aria-hidden="true" className="w-3 h-3" />
              Apply credit
            </button>
          </div>
          <AdminTable
            data={charges}
            columns={chargeColumns}
            enableGlobalFilter
            globalFilterPlaceholder="Search charges (description, status, id)…"
            pageSize={25}
            testId="admin-user-payment-history"
          />
        </div>
      </div>
      <aside className="w-full space-y-4 lg:w-[380px]">
        <Card padded>
          <h3 className="text-sm font-semibold text-slate-900">
            Upcoming invoice
          </h3>
          <div className="mt-3 space-y-2.5">
            <LineRow label="Subtotal" value="$14.99" />
            <LineRow label="Stripe Tax (TX 8.25%)" value="$1.24" />
            <LineRow label="Adjustments" value="$0.00" />
            <hr className="border-slate-200" />
            <LineRow label="Total due Jun 17" value="$16.23" bold />
          </div>
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-50 p-3">
            <Info aria-hidden="true" className="mt-0.5 w-3.5 h-3.5 text-indigo-700" />
            <p className="text-[11px] leading-relaxed text-indigo-800">
              Stripe Tax handles 50+ jurisdictions automatically. No MoR needed.
            </p>
          </div>
        </Card>
        <Card padded>
          <h3 className="text-sm font-semibold text-slate-900">
            Processor routing
          </h3>
          <div className="mt-3 space-y-2">
            <ProcessorRow
              name="Stripe"
              region="Global (default)"
              state="active"
              active
            />
            <ProcessorRow
              name="Razorpay"
              region="India (planned)"
              state="shipped Q3"
              active={false}
            />
          </div>
        </Card>
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center gap-2">
            <Ban aria-hidden="true" className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-xs font-bold text-amber-800">
              Paddle dropped (2026-05-18)
            </span>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-slate-700">
            Merchant application rejected on banking-partner-category grounds.
            Stripe-only stack; Razorpay planned for India.
          </p>
        </div>
      </aside>
    </div>
  );
}

function PlanActionButton({
  icon: Icon,
  label,
  kind,
}: {
  icon: typeof CircleArrowUp;
  label: string;
  kind?: "primary" | "danger";
}) {
  const base = "flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-semibold";
  const cls =
    kind === "primary"
      ? "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
      : kind === "danger"
        ? "border-red-300 bg-white text-red-700 hover:bg-red-50"
        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50";
  return (
    <button type="button" className={`${base} ${cls}`}>
      <Icon aria-hidden="true" className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}


function LineRow({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className={`text-xs ${bold ? "font-semibold text-slate-900" : "font-medium text-slate-500"}`}>
        {label}
      </span>
      <div className="flex-1" />
      <span className={`${bold ? "text-base font-bold text-slate-900" : "text-xs font-semibold text-slate-900"}`}>
        {value}
      </span>
    </div>
  );
}

function ProcessorRow({
  name,
  region,
  state,
  active,
}: {
  name: string;
  region: string;
  state: string;
  active: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
      <div
        aria-hidden="true"
        className={`flex h-7 w-7 items-center justify-center rounded-md ${active ? "bg-emerald-600" : "bg-slate-400 opacity-60"}`}
      >
        <CreditCard aria-hidden="true" className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-semibold text-slate-900">
          {name}
        </div>
        <div className="truncate text-[11px] text-slate-500">{region}</div>
      </div>
      <span
        className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
          active
            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
            : "border-slate-200 bg-white text-slate-500"
        }`}
      >
        {state}
      </span>
    </div>
  );
}

export function EntitlementsTab({ ents }: { ents: Entitlement[] }) {
  const columns = useMemo<ColumnDef<Entitlement, unknown>[]>(
    () => [
      {
        id: "tier",
        accessorKey: "tier",
        header: "Tier",
        cell: ({ row }) => {
          const e = row.original;
          const Icon = tierIcon(e.tier);
          return (
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100">
                <Icon aria-hidden="true" className="h-3 w-3 text-slate-700" />
              </div>
              <span className="text-sm font-semibold text-slate-900">
                {tierLabel(e.tier)}
              </span>
            </div>
          );
        },
      },
      {
        id: "state",
        accessorFn: (e) => e.stateCode ?? e.countryCode ?? "",
        header: "State / Country",
        cell: ({ row }) => {
          const e = row.original;
          return (
            <span className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-[11px] font-semibold text-slate-700">
              {e.stateCode ?? e.countryCode ?? "—"}
            </span>
          );
        },
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Source",
        cell: ({ row }) => {
          const e = row.original;
          return (
            <span
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                e.source === "trial"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-indigo-50 text-indigo-700"
              }`}
            >
              {e.source}
            </span>
          );
        },
      },
      {
        id: "expires",
        accessorKey: "expiresAt",
        header: "Expires",
        cell: ({ row }) => {
          const e = row.original;
          if (e.expiresAt > "2099") {
            return (
              <div className="flex items-center gap-1.5">
                <InfinityIcon
                  aria-hidden="true"
                  className="h-3.5 w-3.5 text-indigo-600"
                />
                <span className="text-xs font-semibold text-slate-900">
                  never
                </span>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1.5">
              <Calendar
                aria-hidden="true"
                className="h-3 w-3 text-slate-500"
              />
              <span className="font-mono text-[11px] font-medium text-slate-900">
                {e.expiresAt.replace("T", " ").replace("Z", " UTC")}
              </span>
            </div>
          );
        },
      },
      {
        id: "granted",
        accessorKey: "grantedAt",
        header: "Granted",
        cell: ({ row }) => (
          <span className="text-[11px] text-slate-500">
            {row.original.grantedAt.slice(0, 10)} · {row.original.grantedBy}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={`Revoke ${tierLabel(row.original.tier)} ${row.original.stateCode ?? row.original.countryCode ?? ""}`.trim()}
              className="rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              Revoke
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="flex-1 space-y-4">
        <Card padded>
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-slate-900">
              Trial extension
            </h3>
            <div className="flex-1" />
            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">
              Current: 2026-06-17
            </span>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_220px]">
            <Field label="Extend by">
              <SelectMock value="30 days" />
            </Field>
            <Field label="New expiry">
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5">
                <span className="flex-1 font-mono text-sm font-medium text-slate-900">
                  2026-07-17 21:16 UTC
                </span>
                <Calendar aria-hidden="true" className="w-3 h-3 text-slate-500" />
              </div>
            </Field>
            <Field label=" ">
              <button
                type="button"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                <CalendarPlus aria-hidden="true" className="w-3.5 h-3.5" />
                Extend trial
              </button>
            </Field>
          </div>
          <div className="mt-4">
            <label className="block text-[11px] font-semibold text-slate-700">
              Reason (required, written to audit log)
            </label>
            <div className="mt-1.5 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-900">
              Internal testing — extending while we build out content + paywall
              functionality
            </div>
          </div>
        </Card>
        <div className="space-y-3">
          <div className="flex items-center gap-3 px-1">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Entitlements
              </h3>
              <div className="text-[11px] text-slate-500">
                {ents.length} active · {ents.filter((e) => e.tier === "class_c").length} Class C ·{" "}
                {ents.filter((e) => e.tier === "cdl").length} CDL ·{" "}
                {ents.filter((e) => e.tier === "tourist_in").length} Tourist-In
              </div>
            </div>
            <div className="flex-1" />
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
            >
              <Plus aria-hidden="true" className="w-3 h-3" />
              Grant comp
            </button>
          </div>
          <AdminTable
            data={ents}
            columns={columns}
            enableGlobalFilter
            globalFilterPlaceholder="Search entitlements (tier, state, source)…"
            pageSize={25}
            testId="admin-user-entitlements"
          />
        </div>
      </div>
      <aside className="w-full space-y-4 lg:w-[380px]">
        <div className="rounded-xl border border-indigo-300 bg-indigo-50 p-5">
          <div className="flex items-center gap-2">
            <Gift aria-hidden="true" className="w-4 h-4 text-indigo-700" />
            <h3 className="text-sm font-bold text-indigo-700">
              Grant comp entitlement
            </h3>
          </div>
          <div className="mt-3 space-y-3">
            <Field label="Tier" tone="indigo">
              <SelectMock value="Class C" />
            </Field>
            <Field label="State" tone="indigo">
              <SelectMock value="WA — Washington" />
            </Field>
            <Field label="Expires" tone="indigo">
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  ["Never", true],
                  ["+30d", false],
                  ["+90d", false],
                  ["Pick…", false],
                ].map(([lbl, act]) => (
                  <button
                    key={String(lbl)}
                    type="button"
                    className={`rounded-lg border px-2 py-2 text-[11px] font-semibold ${
                      act
                        ? "border-indigo-600 bg-indigo-600 text-white"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    {String(lbl)}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Reason (audit log)" tone="indigo">
              <textarea
                className="h-16 w-full rounded-lg border border-slate-200 p-3 text-xs text-slate-900"
                defaultValue="Adding WA so this tester can validate the WA → TX rule-diff flow before we ship the cross-state pack."
              />
            </Field>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              <Check aria-hidden="true" className="w-3.5 h-3.5" />
              Grant entitlement
            </button>
          </div>
        </div>
        <Card padded>
          <h4 className="text-xs font-bold text-slate-700">Schema notes</h4>
          <ul className="mt-2 space-y-1.5 text-[11px] leading-relaxed text-slate-500">
            <li>
              <span className="mr-2 inline-block h-1 w-1 rounded-full bg-slate-300 align-middle" />
              expires_at is NOT NULL — &apos;never&apos; = 2099-12-31 sentinel.
            </li>
            <li>
              <span className="mr-2 inline-block h-1 w-1 rounded-full bg-slate-300 align-middle" />
              Class C / CDL / motorcycle: state_code required (unless
              source=trial).
            </li>
            <li>
              <span className="mr-2 inline-block h-1 w-1 rounded-full bg-slate-300 align-middle" />
              tourist_in: state_code AND country_code must be NULL (flat tier).
            </li>
            <li>
              <span className="mr-2 inline-block h-1 w-1 rounded-full bg-slate-300 align-middle" />
              Revoke sets expires_at = NOW() — row stays for audit trail.
            </li>
          </ul>
        </Card>
      </aside>
    </div>
  );
}

function Field({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: "indigo";
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        className={`block text-[11px] font-semibold ${
          tone === "indigo" ? "text-indigo-700" : "text-slate-700"
        }`}
      >
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SelectMock({ value }: { value: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5">
      <span className="flex-1 text-sm font-medium text-slate-900">{value}</span>
      <ChevronDown aria-hidden="true" className="w-3 h-3 text-slate-500" />
    </div>
  );
}
